import { useEffect, useState } from 'react';

import { Button } from '../../atoms/Button';

export interface RetryableErrorPageProps {
  title: string;
  body: string;
  /** Seconds until an automatic retry; counts down to "Retrying…". Re-key to restart. */
  countdown?: number;
  /** Shows a Retry button when passed. */
  onRetry?: () => void;
}

/** A full-screen error with an optional automatic-retry countdown and a manual Retry. */
export function RetryableErrorPage({ title, body, countdown, onRetry }: RetryableErrorPageProps) {
  const [secondsLeft, setSecondsLeft] = useState(countdown);

  useEffect(() => {
    if (!secondsLeft) return;
    const id = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <h1 className="text-6xl font-bold">{title}</h1>
      <p className="mt-4 text-xl text-muted-foreground">{body}</p>
      {secondsLeft !== undefined && (
        <p className="mt-2 text-muted-foreground" role="status">
          {secondsLeft > 0 ? `Retrying in ${secondsLeft} s…` : 'Retrying…'}
        </p>
      )}
      {onRetry && (
        <Button className="mt-6" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

RetryableErrorPage.displayName = 'RetryableErrorPage';
